import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Typography,
  InputAdornment,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tableHeadCellSx, tokens } from '../../theme/tokens';

export default function StaffList() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchStaff();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchStaff();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/staff', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
        },
      });
      setStaff(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch staff');
      console.error('Error fetching staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }
    try {
      await api.delete(`/staff/${id}`);
      fetchStaff();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete staff');
    }
  };

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>HR records</Box>
          </>
        }
        title="HR records"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/staff/new')}
            sx={{ bgcolor: tokens.primary, '&:hover': { bgcolor: tokens.primaryStrong } }}
          >
            New record
          </Button>
        }
      />

      {location.state?.message && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '9px' }} onClose={() => navigate('.', { replace: true, state: {} })}>
          {location.state.message}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: '9px',
            bgcolor: tokens.dangerSoft,
            color: '#7C2E1D',
            border: '1px solid #EFCFC5',
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            flexWrap: 'wrap',
            alignItems: 'center',
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <TextField
            placeholder="Search staff…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{
              minWidth: 220,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': { borderColor: tokens.border },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: tokens.muted }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: tokens.primary }} />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Staff</TableCell>
                    <TableCell sx={tableHeadCellSx}>Email</TableCell>
                    <TableCell sx={tableHeadCellSx}>Phone</TableCell>
                    <TableCell sx={tableHeadCellSx}>Region</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Box sx={{ py: 5.5, color: tokens.muted }}>
                          <Typography sx={{ fontWeight: 600, color: tokens.ink, mb: 0.5 }}>
                            No staff records
                          </Typography>
                          <Typography variant="body2">
                            Add a staff record to get started.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    staff.map((member) => (
                      <TableRow
                        key={member.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#F4F7F5' },
                          '& td': { borderColor: tokens.border, py: 1.5 },
                        }}
                        onClick={() => navigate(`/dashboard/staff/${member.id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.35 }}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                bgcolor: tokens.primary,
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {member.first_name?.[0] || 'S'}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 500, fontSize: 13.5 }}>
                                {member.first_name} {member.last_name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13.5 }}>{member.email}</TableCell>
                        <TableCell sx={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>
                          {member.phone || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13.5 }}>{member.region || '—'}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/dashboard/staff/${member.id}`)}
                            title="View"
                            sx={{ color: tokens.primary }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/dashboard/staff/${member.id}`)}
                            title="Edit"
                            sx={{ color: tokens.muted }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(member.id)}
                            title="Delete"
                            sx={{ color: tokens.danger }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  borderTop: `1px solid ${tokens.border}`,
                  color: tokens.muted,
                  fontSize: 13,
                }}
              >
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{ color: tokens.muted }}
                />
              </Box>
            </>
          )}
        </TableContainer>
      </Paper>
    </Box>
  );
}
